import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Floor, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { Oval, PlotFunction } from "@pdfking/renderer-api"
import { Range, Constant } from "@pdfking/tfunc"

import { DOT, LINE_DOT, LINE_DOT_PITCH, LINE_STROKE, MILLIMETER_STROKE, PALETTE } from "#constants"

import { NotebookBackground } from "../types/NotebookBackground"
import { PlotLine as line } from "./PlotLine"

// `count` steps of `pitch` from `from`, both ends included
const steps = (from: Distance, pitch: Distance, count: number): Distance[] =>
  [...Range(0, count)].map(i => Total(from, Scale(pitch, i)))

// Where `pitch` fits whole along `length` from `start`, the rest split either
// side: its first step and how many steps fit
const centered = (start: Distance, length: Distance, pitch: Distance) => {
  const count = Floor(length / pitch)
  return { count, from: Total(start, Scale(Subtract(length, Scale(pitch, count)), 0.5)) }
}

const dot = (x: Distance, y: Distance, across: Distance): PageShape => ({
  closed: true,
  enum: PlotFunction.PLOT_PATH,
  fill_color: PALETTE.lines,
  points: Oval({ x, y }, Scale(across, 0.5)),
})

/** `box` filled with `background`, `pitch` apart, in the lines' gray and at
 * the shop's notepads' sizes (see constants/Lines), stroked as they stroke
 * them: lines across it, or lines
 * of dots, down from its top as many as fit, the first a pitch under it; a
 * grid of dots or of squares as many whole as fit, centered; a millimetre
 * paper's lines, heavier every fifth, of whole fifths, centered; or nothing. */
export const PlotNotebookBackground = ({
  background,
  box,
  pitch,
}: {
  background: NotebookBackground
  box: SpatialRectangle<Distance>
  pitch: Distance
}): PageShape[] => {
  // The lines down from the top, their middles
  const top = Total(box.y, box.height)
  const lines = [...Range(1, Floor(box.height / pitch))].map(i => Subtract(top, Scale(pitch, i)))
  if (background === NotebookBackground.LINES)
    return lines.map(y => line(box.x, y, box.width, ZERO_DISTANCE, LINE_STROKE))
  if (background === NotebookBackground.DOTTED_LINES)
    return lines.flatMap(y =>
      [...Range(Floor(box.width / LINE_DOT_PITCH))].map(j =>
        dot(Total(box.x, Scale(LINE_DOT, 0.5), Scale(LINE_DOT_PITCH, j)), y, LINE_DOT)
      )
    )
  // A grid's: a millimetre paper's of whole fifths
  const unit = background === NotebookBackground.MILLIMETER ? Scale(pitch, 5) : pitch
  const across = centered(box.x, box.width, unit)
  const down = centered(box.y, box.height, unit)
  const per = unit / pitch
  const columns = steps(across.from, pitch, across.count * per)
  const rows = steps(down.from, pitch, down.count * per)
  const grid = (stroke: (i: number) => Distance) => [
    ...columns.map((x, i) => line(x, rows[0]!, ZERO_DISTANCE, Scale(unit, down.count), stroke(i))),
    ...rows.map((y, i) =>
      line(columns[0]!, y, Scale(unit, across.count), ZERO_DISTANCE, stroke(i))
    ),
  ]
  if (background === NotebookBackground.SQUARES) return grid(Constant(LINE_STROKE))
  if (background === NotebookBackground.MILLIMETER)
    return grid(i => (i % 5 ? MILLIMETER_STROKE : LINE_STROKE))
  if (background === NotebookBackground.DOTS)
    return rows.flatMap(y => columns.map(x => dot(x, y, DOT)))
  return []
}
